export interface UnitConversion {
  from: string;
  to: string;
  factor: number;
}

export interface UnitGroup {
  name: string;
  baseUnit: string;
  units: {
    value: string;
    label: string;
    factor: number; // conversion factor relative to base unit
  }[];
}

export const unitGroups: UnitGroup[] = [
  {
    name: "Weight",
    baseUnit: "g",
    units: [
      { value: "mg", label: "Milligrams (mg)", factor: 0.001 },
      { value: "g", label: "Grams (g)", factor: 1 },
      { value: "kg", label: "Kilograms (kg)", factor: 1000 },
      { value: "oz", label: "Ounces (oz)", factor: 28.3495 },
      { value: "lb", label: "Pounds (lb)", factor: 453.592 },
    ],
  },
  {
    name: "Volume",
    baseUnit: "ml",
    units: [
      { value: "ml", label: "Milliliters (ml)", factor: 1 },
      { value: "cl", label: "Centiliters (cl)", factor: 10 },
      { value: "l", label: "Liters (l)", factor: 1000 },
      { value: "fl_oz", label: "Fluid Ounces (fl oz)", factor: 29.5735 },
      { value: "gal", label: "Gallons (gal)", factor: 3785.41 },
    ],
  },
  {
    name: "Quantity",
    baseUnit: "pcs",
    units: [
      { value: "pcs", label: "Pieces (pcs)", factor: 1 },
      { value: "dozen", label: "Dozen", factor: 12 },
      { value: "box", label: "Box", factor: 1 },
      { value: "pack", label: "Pack", factor: 1 },
      { value: "case", label: "Case", factor: 1 },
    ],
  },
];

export const convertUnit = (
  value: number,
  fromUnit: string,
  toUnit: string
): number => {
  if (fromUnit === toUnit) return value;

  // Find the unit groups for both units
  const fromGroup = unitGroups.find((group) =>
    group.units.some((unit) => unit.value === fromUnit)
  );
  const toGroup = unitGroups.find((group) =>
    group.units.some((unit) => unit.value === toUnit)
  );

  // If units are not in the same group or not found, return original value
  if (!fromGroup || !toGroup || fromGroup.name !== toGroup.name) {
    return value;
  }

  // Get conversion factors
  const fromFactor =
    fromGroup.units.find((unit) => unit.value === fromUnit)?.factor || 1;
  const toFactor =
    toGroup.units.find((unit) => unit.value === toUnit)?.factor || 1;

  // Convert to base unit then to target unit
  return (value * fromFactor) / toFactor;
};

export const formatUnitValue = (value: number, unit: string): string => {
  const group = unitGroups.find((g) => g.units.some((u) => u.value === unit));
  if (!group) return `${value} ${unit}`;

  // Find the most appropriate unit in the group
  const unitInfo = group.units.find((u) => u.value === unit);
  if (!unitInfo) return `${value} ${unit}`;

  // Format number based on magnitude
  if (value >= 1000) {
    return `${value.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${
      unitInfo.label
    }`;
  } else if (value < 0.01) {
    return `${value.toLocaleString(undefined, { maximumFractionDigits: 4 })} ${
      unitInfo.label
    }`;
  } else {
    return `${value.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${
      unitInfo.label
    }`;
  }
};

export const findBestUnit = (value: number, group: UnitGroup): string => {
  const baseValue =
    value * (group.units.find((u) => u.value === group.baseUnit)?.factor || 1);

  let bestUnit = group.baseUnit;
  let bestFactor = 1;

  group.units.forEach((unit) => {
    const convertedValue = baseValue / unit.factor;
    if (convertedValue >= 1 && convertedValue < 1000) {
      bestUnit = unit.value;
      bestFactor = unit.factor;
    }
  });

  return bestUnit;
};

export const getUnitOptions = (
  currentUnit: string
): { value: string; label: string }[] => {
  const group = unitGroups.find((g) =>
    g.units.some((u) => u.value === currentUnit)
  );
  if (!group) return [];

  return group.units.map(({ value, label }) => ({ value, label }));
};

export const getUnitGroup = (unit: string): UnitGroup | undefined => {
  return unitGroups.find((group) => group.units.some((u) => u.value === unit));
};
